import React, { useCallback } from 'react';
import { useChatStore } from '../../store/chatStore';
import { CenteredInputForm } from '../CenteredInput';

interface ChatInputProps {
  dropUpMenu?: boolean;
  isCompact?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  dropUpMenu,
  isCompact,
}) => {
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );

  const addMessage = useChatStore((state) => state.addMessage);
  const isLoading = useChatStore((state) =>
    Boolean(
      state.activeConversationId
        ? state.conversations[state.activeConversationId]?.isLoading
        : false,
    ),
  );

  const handleSubmit = useCallback(
    async (
      text: string,
      options?: {
        selectedToolGroups?: string[];
        enableReasoning?: boolean;
        attachedFiles?: import('../../store/chatStore').ContentBlock[];
        contextItems?: import('../../store/chatStore').ContextItem[];
      },
    ) => {
      if (
        !text &&
        (!options?.attachedFiles || options.attachedFiles.length === 0)
      ) {
        return;
      }

      const store = useChatStore.getState();

      // Set the input prompt and any attached files in the store
      store.setInputPrompt(text);

      // Set selected tool groups if provided
      if (options?.selectedToolGroups) {
        store.setSelectedToolGroups(options.selectedToolGroups);
      }

      // Handle attached files by converting to content blocks and adding to store
      if (options?.attachedFiles && options.attachedFiles.length > 0) {
        // For now, we'll need to handle files differently since submitInputPrompt doesn't handle files
        // We'll use the existing addMessage approach for files but include context

        // Get attached context items from store
        const attachedContextItems = store.attachedContextItems;

        // Build content blocks array
        const contentBlocks: import('../../store/chatStore').ContentBlock[] =
          [];

        // Add text content if provided, including context
        if (text) {
          let formattedText = text;
          if (attachedContextItems.length > 0) {
            const contextString = attachedContextItems
              .map((item) => item.prompt)
              .join('\n');
            formattedText += `\n<context>${contextString}</context>`;
          }
          contentBlocks.push({ type: 'text', text: formattedText });
        }

        // Add file content blocks
        contentBlocks.push(...options.attachedFiles);

        if (!activeConversationId) {
          const newId = store.createConversation();

          if (options?.enableReasoning) {
            store.toggleReasoningEnabled(newId, true);
          }

          addMessage(newId, {
            content: contentBlocks,
            role: 'user',
            selectedToolGroups: options?.selectedToolGroups,
          });
        } else {
          addMessage(activeConversationId, {
            content: contentBlocks,
            role: 'user',
            selectedToolGroups: options?.selectedToolGroups,
          });
        }

        // Clear the input and context after submission
        store.setInputPrompt('');
        store.setAttachedContextItems([]);
      } else {
        // No files, use the store's submitInputPrompt which properly handles context
        await store.submitInputPrompt();
      }
    },
    [activeConversationId, addMessage],
  );

  return (
    <div className="sticky bottom-0 z-10 w-full bg-white px-4 pb-1 dark:bg-gray-900">
      <div className="mx-auto w-full max-w-3xl">
        <CenteredInputForm
          isLoading={isLoading}
          onSubmit={handleSubmit}
          dropUpMenu={dropUpMenu}
          isSmallWindow={isCompact}
        />
      </div>
    </div>
  );
};
